import { createSelector, createSlice } from "@reduxjs/toolkit";
import K from "utilities/constants";
import {
  selectClientSelection,
  selectSelectedClientIds,
} from "./globalSelectorSlice";

const selectRawSelectedRecruiters = (state) =>
  state.globalSelector.selectedRecruiters;

const { Redux } = K;

const initialState = {
  [Redux.Roles]: [],
  [Redux.Recruiters]: [],
  [Redux.Clients]: [],
  [Redux.Details]: null,
  [Redux.HomeRoute]: null,
};

export const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setLoggedInUser: (state, action) => {
      const { data, homeRoute } = action.payload;
      const { totalClients, roles, ...details } = data.basicDetail;
      state[Redux.Details] = details;
      state[Redux.Roles] = roles;
      state[Redux.TotalClients] = totalClients;
      state[Redux.Recruiters] = data?.recruiters || [];

      if (details.isSuperAdmin) {
        state[Redux.HomeRoute] = homeRoute;
        state[Redux.Clients] = data.clients;
      } else {
        state[Redux.HomeRoute] = homeRoute;
        state[Redux.Clients] = data.basicDetail.clients;
      }
    },
    setUserDetails: (state, action) => {
      state[Redux.Details] = action.payload;
    },
    addClient: (state, action) => {
      state[Redux.Clients] = [...state[Redux.Clients], action.payload];
    },
    updateUserDetails: (state, action) => {
      state[Redux.Details] = { ...state[Redux.Details], ...action.payload };
    },
    updateClient: (state, action) => {
      const { id, updates } = action.payload;

      state[Redux.Clients] = state[Redux.Clients].map((c) =>
        c.id === id ? { ...c, ...updates } : c,
      );
    },
  },
});

export const selectUser = (state) => state.user;

export const selectUserDetails = createSelector(
  selectUser,
  (user) => user.details,
);

export const selectUserRecruiters = createSelector(
  selectUser,
  (user) => user.recruiters,
);

export const selectUserClients = createSelector(
  selectUser,
  (user) => user.clients,
);

export const selectAvailableRecruiters = createSelector(
  selectUserRecruiters,
  selectClientSelection,
  selectSelectedClientIds,
  (recruiters, selection, selectedClientIds) => {
    if (selection === K.Clients.SelectionType.All) {
      return recruiters;
    }

    return recruiters.filter((r) =>
      r.clients?.some((c) => selectedClientIds.includes(c.id)),
    );
  },
);

export const selectSelectedRecruiters = createSelector(
  selectAvailableRecruiters,
  selectRawSelectedRecruiters,
  (availableRecruiters, selectedRecruiters) => {
    const selectedSet = new Set(selectedRecruiters);
    return availableRecruiters.filter((r) => selectedSet.has(r.id));
  },
);

export const selectAvailableClients = createSelector(
  selectClientSelection,
  selectSelectedClientIds,
  selectUserClients,
  (selection, selectedClients, assignedClients) => {
    if (selection === K.Clients.SelectionType.All) {
      return assignedClients;
    }

    return selectedClients.map((c) => assignedClients.find((a) => a.id === c));
  },
);

export const selectUserRoles = createSelector(selectUser, (user) => user.roles);

export const selectUserId = createSelector(
  selectUser,
  (user) => user.details.id,
);

export const selectClientById = createSelector(
  selectUser,
  (_, id) => id,
  (user, id) => user.clients.find((c) => c.id === id),
);

export const selectIsClientHasEngagementMilestones = createSelector(
  selectClientById,
  (client) => client?.engagementMilestonesCount > 0,
);

export const selectIsUserTermsAccepted = createSelector(
  selectUserDetails,
  (details) => !!details?.isTermsAccepted,
);

export const {
  setLoggedInUser,
  setUserDetails,
  updateUserDetails,
  updateClient,
  addClient,
} = userSlice.actions;

export default userSlice.reducer;
